import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FaFacebook, 
  FaTwitter, 
  FaInstagram, 
  FaLinkedin, 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt,
  FaArrowLeft 
} from 'react-icons/fa';

const Footer: React.FC<{ language: 'ar' | 'en' }> = ({ language }) => {
  const currentYear = new Date().getFullYear();
  const isRTL = language === 'ar';

  const quickLinks = [
    { nameAr: 'الرئيسية', nameEn: 'Home', path: '/' },
    { nameAr: 'من نحن', nameEn: 'About Us', path: '/about' },
    { nameAr: 'خدماتنا', nameEn: 'Our Services', path: '/services' },
    { nameAr: 'مشاريعنا', nameEn: 'Our Projects', path: '/projects' },
    { nameAr: 'الأخبار', nameEn: 'News', path: '/news' },
    { nameAr: 'اتصل بنا', nameEn: 'Contact Us', path: '/contact' }
  ];

  const services = [
    { nameAr: 'المقاولات العامة', nameEn: 'General Contracting' },
    { nameAr: 'استخراج المحاجر', nameEn: 'Quarry Extraction' },
    { nameAr: 'البنية التحتية', nameEn: 'Infrastructure' },
    { nameAr: 'التصميم المعماري', nameEn: 'Architectural Design' },
    { nameAr: 'الصيانة والتشغيل', nameEn: 'Maintenance & Operation' },
    { nameAr: 'الاستشارات الهندسية', nameEn: 'Engineering Consultations' }
  ];

  const socialLinks = [
    { icon: FaFacebook, href: '#', label: 'Facebook' },
    { icon: FaTwitter, href: '#', label: 'Twitter' },
    { icon: FaInstagram, href: '#', label: 'Instagram' },
    { icon: FaLinkedin, href: '#', label: 'LinkedIn' }
  ];

  return (
    <footer className={`bg-gray-900 text-white ${isRTL ? 'font-cairo' : 'font-montserrat'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Main Footer Content */}
      <div className="container-custom py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1 flex flex-col justify-start h-full">
            <div className="flex flex-col items-center mb-4 mt-0 lg:mt-2">
              <img src="/images/logo.png" alt="Mecca El Baraka Logo" className="w-64 h-64 object-contain mb-2 mx-auto" />
            </div>
            <p className="text-gray-300 mb-6 leading-relaxed text-center">
              {language === 'ar'
                ? 'شركة رائدة في مجال البناء والتشييد، نقدم حلولاً متكاملة في المقاولات واستخراج المحاجر بأعلى معايير الجودة.'
                : 'A leading company in construction and contracting, we provide integrated solutions in contracting and quarry extraction with the highest quality standards.'}
            </p>
            {/* Social Links */}
            <div className="flex gap-6 rtl:space-x-reverse justify-center">
              {/* فقط LinkedIn و Facebook */}
              <a
                href="#"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:bg-primary-600 hover:text-white transition-colors"
                aria-label="Facebook"
              >
                <FaFacebook className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:bg-primary-600 hover:text-white transition-colors"
                aria-label="LinkedIn"
              >
                <FaLinkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-6">{language === 'ar' ? 'روابط سريعة' : 'Quick Links'}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-primary-400 transition-colors flex items-center space-x-2 rtl:space-x-reverse group"
                  >
                    <FaArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
                    <span>{isRTL ? link.nameAr : link.nameEn}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold mb-6">{language === 'ar' ? 'خدماتنا' : 'Our Services'}</h4>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <Link
                    to="/services"
                    className="text-gray-300 hover:text-primary-400 transition-colors flex items-center space-x-2 rtl:space-x-reverse group"
                  >
                    <FaArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
                    <span>{isRTL ? service.nameAr : service.nameEn}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-6">{language === 'ar' ? 'معلومات الاتصال' : 'Contact Information'}</h4>
            <div className="space-y-4">
              <div className="flex items-start space-x-3 rtl:space-x-reverse">
                <FaMapMarkerAlt className="w-5 h-5 text-primary-400 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-white">{language === 'ar' ? 'العنوان' : 'Address'}</h4>
                  <p className="text-gray-300">
                    38 الملتقي العربي - شيراتون<br />
                    {language === 'ar' ? 'القاهرة، مصر' : 'Cairo, Egypt'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <FaPhone className="w-5 h-5 text-primary-400 flex-shrink-0" />
                <a href="tel:020224181844" className="text-gray-300 hover:text-primary-400 transition-colors">
                  020224181844
                </a>
              </div>
              
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <FaEnvelope className="w-5 h-5 text-primary-400 flex-shrink-0" />
                <a href="mailto:info@meccaelbaraka.com" className="text-gray-300 hover:text-primary-400 transition-colors">
                  info@meccaelbaraka.com
                </a>
              </div>
            </div>

            {/* Newsletter Signup */}
            <div className="mt-8">
              <h5 className="font-semibold mb-3">{language === 'ar' ? 'اشترك في النشرة الإخبارية' : 'Subscribe to Newsletter'}</h5>
              <div className="flex">
                <input
                  type="email"
                  placeholder={language === 'ar' ? 'بريدك الإلكتروني' : 'Your email'}
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-r-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-l-lg transition-colors">
                  {language === 'ar' ? 'اشتراك' : 'Subscribe'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © {currentYear} {language === 'ar' ? 'شركة مكة البركة للمقاولات العامة. جميع الحقوق محفوظة.' : 'Mecca El Baraka For Construction. All rights reserved.'}
            </div>
            
            <div className="flex space-x-6 rtl:space-x-reverse text-sm">
              <Link to="/privacy" className="text-gray-400 hover:text-primary-400 transition-colors">
                {language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-primary-400 transition-colors">
                {language === 'ar' ? 'شروط الاستخدام' : 'Terms of Use'}
              </Link>
              <Link to="/sitemap" className="text-gray-400 hover:text-primary-400 transition-colors">
                {language === 'ar' ? 'خريطة الموقع' : 'Sitemap'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 